import update from 'immutability-helper';
import React from 'react';
import { failureModeCreateSchema } from '../../../../../common/src/domain/survey/failureMode/failureModeSchema';
import { findingCreateSchema } from '../../../../../common/src/domain/survey/finding/findingSchema';
import { ITranslateProps, translate } from '../../../mixins/translate';
import { fetchDelete, fetchGet, fetchPost } from '../../../store/fetch';
import {
    ramsAOptions,
    ramsCOptions,
    ramsEcOptions,
    ramsEnvOptions,
    ramsPOptions,
    ramsROptions,
    ramsSOptions,
    getTotalPriorityValue,
    getWeightedPriorityValue,
} from '../../../utils/data/rams';
import { AssetTypes as assetTypes } from '../../../utils/enums/AssetTypes';
import * as htmlHelper from '../../../utils/htmlHelper';
import { IconEditButton } from '../../common/iconButtons/IconEditButton';
import { IconRemoveButton } from '../../common/iconButtons/IconRemoveButton';
import {
    EditPanel,
    EditPanelButton,
    EditPanelButtons,
    EditPanelHeader,
    EditPanelRow,
    PanelButtonLink,
    PanelButtons,
    PanelHeader,
    PanelTable,
    TableBody,
    TableData,
    TableRow,
} from '../../elements';
import { DraggableContainer } from '../../react-smooth-dnd/DraggableContainer';
import { DraggableItem } from '../../react-smooth-dnd/DraggableItem';
import { Component } from '../../utils/Component';
import { showAssetInNewWindow } from '../../utils/showAssetInNewWindow';
import {
    ValidatingEditPanelSaveButton,
    ValidatingForm,
    ValidatingFormContext,
    ValidatingFormInputText,
    ValidatingFormTextArea,
} from '../../validatingElements';
import { AssetDropzone } from '../Common/AssetDropzone';
import { AssetTemplate } from '../Common/AssetTemplate';
import { getAssetOrdersAfterReorder } from '../Common/getAssetOrdersAfterReorder';
import { IAsset } from '../Interfaces/IAsset';
import { RamssheepDropdownFmeca } from './RamsDropdownFMECA';
import { TotalRiskScore } from './TotalRiskScore';

interface IVerificationFailureModeEditProps extends ITranslateProps {
    objectId: string;
    surveyId: string;
    elementId?: undefined | string;
    unitId?: undefined | string;
    manifestationId?: undefined | string;
    onCancel: () => void;
    onSaved: () => Promise<void>;
    failureModeId?: undefined | string;
    onRemove: (id: string) => Promise<void>;
    objectTreeFilterOnElementGroups: boolean;
    elementGroupName?: undefined | string | null;
}

interface IFinding {
    id: string;
    description: string;
}

interface IVerificationFailureModeEditState {
    assets: IAsset[];
    findings: IFinding[];
    isUploading: boolean;
    showFindingEdit: boolean;
    selectedFindingId?: undefined | string;
}

class VerificationFailureModeEditComponent extends Component<IVerificationFailureModeEditProps, IVerificationFailureModeEditState> {
    constructor(props: IVerificationFailureModeEditProps) {
        super(props);

        this.state = {
            assets: [],
            findings: [],
            isUploading: false,
            showFindingEdit: false,
        };
    }

    public override async componentDidMount() {
        await this.init();
    }

    public override async componentDidUpdate(prevProps: IVerificationFailureModeEditProps) {
        if (prevProps.failureModeId !== this.props.failureModeId) {
            await this.init();
        }
    }

    public override render() {
        return (
            <>
                <EditPanel>
                    <ValidatingForm
                        schema={failureModeCreateSchema}
                        createUrl={`/api/surveys/${this.props.surveyId}/failureModes/`}
                        createUrlQueryParameters={{
                            elementId: this.props.elementId,
                            unitId: this.props.unitId,
                            manifestationId: this.props.manifestationId,
                        }}
                        onSubmitted={async () => await this.props.onSaved()}
                        itemUrl={`/api/surveys/${this.props.surveyId}/failureModes/${this.props.failureModeId}`}
                        id={this.props.failureModeId}
                        onChange={async (key, _value, item, onChange) => this.handleItemChanged(key, item, onChange)}
                    >
                        <ValidatingFormContext.Consumer>
                            {(state) => (
                                <>
                                    {' '}
                                    {state.item.analysisRemarks && (
                                        <>
                                            <EditPanelRow>
                                                <EditPanelHeader text={this.props.t('failureMode.analysis')} />
                                            </EditPanelRow>
                                            <EditPanelRow>
                                                <div className="col-md-12">{htmlHelper.newLineToBreak(state.item.analysisRemarks)}</div>
                                            </EditPanelRow>
                                        </>
                                    )}
                                </>
                            )}
                        </ValidatingFormContext.Consumer>

                        <EditPanelRow>
                            <EditPanelHeader text={this.props.t('failureMode.verificationRiskScoreHeader')} />
                        </EditPanelRow>

                        <EditPanelRow>
                            <div className="col-md-8">
                                <RamssheepDropdownFmeca
                                    name="verificationRamsR"
                                    topLabel="R"
                                    header="Betrouwbaarheid"
                                    subText="Manifestatie van risico"
                                    options={ramsROptions}
                                />
                                <RamssheepDropdownFmeca
                                    name="verificationRamsA"
                                    topLabel="A"
                                    header="Beschikbaarheid"
                                    subText="Fractie van tijd dat object niet functioneert"
                                    options={ramsAOptions}
                                />
                                <RamssheepDropdownFmeca
                                    name="verificationRamsS"
                                    topLabel="S"
                                    header="Veiligheid"
                                    subText="ARBO- en verkeersveiligheid"
                                    options={ramsSOptions}
                                />
                                <RamssheepDropdownFmeca
                                    name="verificationRamsC"
                                    topLabel="C"
                                    header="Verzorgingsniveau"
                                    subText="Beeldkwalitieit van het object"
                                    options={ramsCOptions}
                                />
                                <RamssheepDropdownFmeca
                                    name="verificationRamsEc"
                                    topLabel="E"
                                    header="Gevolgkosten"
                                    subText="De totale corectieve kosten"
                                    options={ramsEcOptions}
                                />
                                <RamssheepDropdownFmeca
                                    name="verificationRamsEnv"
                                    topLabel="E"
                                    header="Milieu"
                                    subText="Negatief effect op leefomgevin"
                                    options={ramsEnvOptions}
                                />
                                <RamssheepDropdownFmeca
                                    name="verificationRamsP"
                                    topLabel="P"
                                    header="Imago"
                                    subText="Media en politieke aandacht"
                                    options={ramsPOptions}
                                />

                                {/* <span style={{ float: 'left', marginTop: '5px', marginRight: '10px', fontWeight: 'bold' }}>
                                    {this.props.t('failureMode.riskScoreVerification')}
                                </span> */}
                                {/* {RamssheepDropdownFMECA('verificationRiskScore', '', 'riskScore', true, true, this.props.t)} */}
                            </div>
                            <div className="col-md-4 mt35">
                                <TotalRiskScore step="verification" />
                            </div>
                        </EditPanelRow>

                        <EditPanelRow>
                            <div className="col-md-12">
                                <ValidatingFormTextArea
                                    name="verificationRemarks"
                                    saveOnBlur={true}
                                    placeholder={this.props.t('failureMode.verificationResult')}
                                />
                            </div>
                        </EditPanelRow>

                        <div className="mt10">
                            <EditPanelButtons>
                                <ValidatingEditPanelSaveButton small={true} />
                            </EditPanelButtons>
                        </div>
                    </ValidatingForm>

                    {/* Findings */}
                    <EditPanelRow>
                        <EditPanelHeader text={this.props.t('failureMode.findings')} />
                    </EditPanelRow>

                    {this.state.findings && this.state.findings.length !== 0 && (
                        <PanelTable hover={true}>
                            <TableBody>
                                {this.state.findings.map((finding) => (
                                    <TableRow key={finding.id} isRowSelected={finding.id === this.state.selectedFindingId}>
                                        <TableData>{finding.description} </TableData>
                                        <TableData>
                                            <IconRemoveButton onClick={async () => this.removeFinding(finding.id)} />
                                            <IconEditButton onClick={async () => this.selectFinding(finding.id)} />
                                        </TableData>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </PanelTable>
                    )}

                    {this.state.showFindingEdit ? (
                        <>
                            <ValidatingForm
                                schema={findingCreateSchema}
                                createUrl={`/api/surveys/${this.props.surveyId}/failureModes/${this.props.failureModeId}/findings`}
                                onSubmitted={async () => this.findingSaved()}
                                itemUrl={`/api/surveys/${this.props.surveyId}/failureModes/${this.props.failureModeId}/findings/${this.state.selectedFindingId}`}
                                subForm={true}
                                id={this.state.selectedFindingId}
                            >
                                <div className="mb10">
                                    <ValidatingFormInputText name="description" placeholder={this.props.t('common.description')} />
                                </div>

                                <EditPanelButtons>
                                    <EditPanelButton
                                        small={true}
                                        onClick={async () => this.setState({ selectedFindingId: undefined, showFindingEdit: false })}
                                        text={this.props.t('common.cancel')}
                                    />
                                    <ValidatingEditPanelSaveButton small={true} />
                                </EditPanelButtons>
                            </ValidatingForm>
                        </>
                    ) : (
                        <PanelButtons>
                            <PanelButtonLink
                                onClick={async () => this.showAddFinding()}
                                small={true}
                                primary={true}
                                text={this.props.t('common.addItem', this.props.t('failureMode.finding'))}
                            />
                        </PanelButtons>
                    )}

                    {/* Assets */}
                    <PanelHeader text={this.props.t('common.files')} />

                    <div className="pr20 pl20">
                        <AssetDropzone
                            acceptedFiles=".png,.jpeg,.jpg,.xls,.xlsx,.pdf"
                            onComplete={async () => this.onDropzoneComplete()}
                            objectId={this.props.objectId}
                            surveyId={this.props.surveyId}
                            failureModeId={this.props.failureModeId}
                            autoProcessQueue={true}
                            onSending={async () => this.setState({ isUploading: true })}
                            onQueueComplete={async () => this.setState({ isUploading: false })}
                            maxFileSizeMb={45_000}
                        />
                    </div>
                    {this.state.assets.length !== 0 && (
                        <>
                            <div>
                                <hr className="short alt" />
                            </div>

                            <div className="section row pt10 bg-light dark">
                                <DraggableContainer
                                    onDrop={async (result: any) => this.handleAssetsSorted(result)}
                                    orientation="horizontal"
                                    dragHandleSelector=".column-drag-handle"
                                >
                                    {this.state.assets.map((asset) => (
                                        <DraggableItem key={asset.id} className="col-sm-6 col-md-3">
                                            <AssetTemplate
                                                key={asset.id}
                                                {...asset}
                                                onShowAsset={(e, selectedAsset) => {
                                                    e.preventDefault();
                                                    this.onShowAsset(selectedAsset);
                                                }}
                                                onAssetsChanged={async () => this.handleRemovedAsset()}
                                                onAssetTextChange={this.handleAssetTextChange.bind(this)}
                                                displayType={undefined}
                                                submitting={false}
                                                isEemuaOrApi={false}
                                                canRemove={true}
                                                canAddAnnotations={true}
                                                returnUrl={this.getReturnUrl()}
                                                canAddToReport={true}
                                                canAddToReportDocument={true}
                                                canEditDescription={true}
                                                canCopyAndMove={false}
                                                canSort={false}
                                                surveyId={this.props.surveyId}
                                                objectId={this.props.objectId}
                                                canAddToStartPage
                                            />
                                        </DraggableItem>
                                    ))}
                                </DraggableContainer>
                            </div>
                        </>
                    )}

                    {this.state.assets.length !== 0 && (
                        <div className="text-right">
                            <a
                                href="#"
                                className="btn btn-primary btn-sm fw600 ml10"
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (window.confirm(this.props.t('common.confirmMultiDelete'))) {
                                        void this.removeAllAssets();
                                    }
                                }}
                            >
                                <span className="fa fa-trash-o pr5"></span> {this.props.t('survey.removeAllFiles')}
                            </a>
                        </div>
                    )}
                </EditPanel>
            </>
        );
    }

    private getReturnUrl() {
        let result = `/surveys/${this.props.surveyId}/verification?`;

        if (this.props.elementId) {
            result += `&elementId=${this.props.elementId}`;
        }
        if (this.props.unitId) {
            result += `&unitId=${this.props.unitId}`;
        }
        if (this.props.manifestationId) {
            result += `&manifestationId=${this.props.manifestationId}`;
        }
        if (this.props.failureModeId) {
            result += `&failureModeId=${this.props.failureModeId}`;
        }
        if (this.props.objectTreeFilterOnElementGroups) {
            result += '&filterOnElementGroups=true';
        }
        if (this.props.elementGroupName) {
            result += `&elementGroupName=${this.props.elementGroupName}`;
        }
        return result;
    }

    private async handleItemChanged(key: string, item: any, onChange: (key: any, value: any) => Promise<void>) {
        const keysToWatch = [
            'verificationRamsR',
            'verificationRamsA',
            'verificationRamsS',
            'verificationRamsC',
            'verificationRamsEc',
            'verificationRamsEnv',
            'verificationRamsP',
        ];

        if (keysToWatch.includes(key)) {
            const verificationRamsTotalPriority = getTotalPriorityValue(
                item.verificationRamsR,
                item.verificationRamsA,
                item.verificationRamsS,
                item.verificationRamsC,
                item.verificationRamsEc,
                item.verificationRamsEnv,
                item.verificationRamsP,
            );
            const verificationRamsWeightedPriority = getWeightedPriorityValue(
                item.verificationRamsR,
                item.verificationRamsA,
                item.verificationRamsS,
                item.verificationRamsC,
                item.verificationRamsEc,
                item.verificationRamsEnv,
                item.verificationRamsP,
            );
            await onChange('verificationRamsTotalPriority', verificationRamsTotalPriority.toString());
            await onChange('verificationRamsWeightedPriority', verificationRamsWeightedPriority);
        }
    }

    private async init() {
        if (this.props.failureModeId) {
            await this.loadAssets();
            await this.loadFindings();
        }
    }

    private async removeFinding(findingId: string) {
        await fetchDelete(`/api/surveys/${this.props.surveyId}/failureModes/${this.props.failureModeId}/findings/${findingId}`);
        await this.loadFindings();
    }

    private async findingSaved() {
        await this.setState({ selectedFindingId: undefined, showFindingEdit: false });
        await this.loadFindings();
    }

    private async showAddFinding() {
        await this.setState({ showFindingEdit: true, selectedFindingId: undefined });
    }

    private async selectFinding(id: string) {
        await this.setState({ selectedFindingId: id, showFindingEdit: true });
    }

    private async loadFindings() {
        const result = await fetchGet(`/api/surveys/${this.props.surveyId}/failureModes/${this.props.failureModeId}/findings`);
        await this.setState({ findings: result.json });
    }

    private onShowAsset(asset: IAsset) {
        showAssetInNewWindow(asset);
    }

    private async handleRemovedAsset() {
        await this.loadAssets();
    }

    private async loadAssets() {
        const result = await fetchGet('/api/assets', {
            surveyId: this.props.surveyId,
            types: `${assetTypes.Video},${assetTypes.Image},${assetTypes.Image360},${assetTypes.Document},${assetTypes.MeasurementXLS},${assetTypes.MeasurementPDF},${assetTypes.ImageAnnotation}`,
            failureModeId: this.props.failureModeId,
        });
        await this.setState({ assets: result.json });
    }

    private async removeAllAssets() {
        await fetchDelete('/api/removeAllAssets', {
            surveyId: this.props.surveyId,
            failureModeId: this.props.failureModeId,
        });
        await this.setState({ assets: [] });
    }

    private async handleAssetTextChange(description: string, assetId: string) {
        const elementPos = this.state.assets.map((item: any) => item.id).indexOf(assetId);
        await this.setStatePromised((state) => ({
            assets: update(state.assets, { [elementPos]: { description: { $set: description } } }),
        }));
    }

    private async handleAssetsSorted(sortResult: { removedIndex: number; addedIndex: number }) {
        const assetOrders = getAssetOrdersAfterReorder(this.state.assets, sortResult);
        await fetchPost('/api/assets/order', { surveyId: this.props.surveyId }, { assetOrders });
        await this.loadAssets();
    }

    private async onDropzoneComplete() {
        await this.loadAssets();
    }
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const VerificationFailureModeEdit = translate()(VerificationFailureModeEditComponent);
